'use strict'
import {OrdinalScale, ScaleBuilder} from './Scales.js';
import {COLUMN_TYPE} from '../TableColumn.js';

export class ChartDataBuilder {
	/**
	 * @param {VizData} vizData
	 */
	constructor(vizData) {
		this._vizData = vizData;
		this._lastResult = ChartDataSet.EMPTY();
	}

	getPointMeta(rowId) {
		let row = this._vizData.getRowObject(rowId);
		let result = this._vizData.getValues(rowId,
			[this.lastResult.x.name, this.lastResult.y.name, this.lastResult.color.name, this.lastResult.size.name]);
		return {
			injection: row.injection.name,
			substance: row.peak && row.peak.substance ? row.peak.substance.displayName : null,
			base: row.peak && row.peak.isUnknown ? row.peak.base : null,
			x: {name: this.lastResult.x.name, value: result[0]},
			y: {name: this.lastResult.y.name, value: result[1]},
			color: {name: this.lastResult.color.name, value: result[2]},
			size: {name: this.lastResult.size.name, value: result[3]},
		}
	}

	/**
	 * @param {{x:string|null, y:string|null, color?:string|null, size?:string|null, connections?:string|null}} params
	 * @return {{
	 * 	ids:string[],
	 * 	x:{type:string, values:any[]},
	 * 	y:{type:string, values:any[]},
	 * 	color:{type:string, values:any[]},
	 * 	size:{type:string, values:any[]},
	 * 	connections:{group: number, ids:string[]}[]
	 * 	}}
	 */
	build(params) {
		let table = this._vizData;
		let columnNames = [params.x, params.y, params.size, params.color, params.connections];
		let types = table.getColumns().getTypes(columnNames);
		const useSizeColumn = types[2] !== null, useColorColumn = types[3] !== null;
		const dataset = new ChartDataSet(columnNames, types);
		if (dataset.x.type === null || dataset.y.type === null)
			return this._lastResult = ChartDataSet.EMPTY();
		for (let i = 0; i < table.getRowsCount(); i++) {
			const datum = table.getValues(i, columnNames)
			dataset.validateAndPut(i, datum);
		}

		let colorFn = (val) => {return "#4e79a7"}
		if (useColorColumn)
			colorFn = ScaleBuilder.computeColorScale(dataset.color.type, dataset.color.values);
		dataset.color.values = dataset.color.values.map(val => colorFn(val))

		const sizeRange = [3, 12];
		let sizeFn = {toPx: (_) => {return sizeRange[0]}}
		if (useSizeColumn)
			sizeFn = ScaleBuilder.computeSizeScale(dataset.size.type, dataset.size.values, sizeRange);
		dataset.size.values = dataset.size.values.map(val => sizeFn.toPx(val))
		return this._lastResult = dataset;
	}

	get lastResult(){
		return this._lastResult;
	}

	get vizData(){
		return this._vizData;
	}
}

export class ChartDataSet {
	constructor(names = [null, null, null, null, null], types = [null, null, null, null, null]) {
		this.ids = [];
		this.x = {type: types[0], name: names[0], values: []};
		this.y = {type: types[1], name: names[1], values: []};
		this.size = {type: types[2], name: names[2], values: []};
		this.color = {type: types[3], name: names[3], values: []};
		this.connections = new Map();
		this._createConnections = names[4] !== null;
	}

	static EMPTY(){
		return new ChartDataSet();
	}

	validateAndPut(id, datum){
		if (!this._isPointMeetsCriteria(datum)) return;
		this.ids.push(id);
		this.x.values.push(datum[0]);
		this.y.values.push(datum[1]);
		this.size.values.push(datum[2]);
		this.color.values.push(datum[3]);
		if (this._createConnections){
			let key = OrdinalScale.normalizeValue(datum[4]);
			if (!this.connections.has(key))
				this.connections.set(key, []);
			this.connections.get(key).push(id)
		}
	}

	getVisiblePoints() {
		const x = [], y = [];
		for (let i = 0; i < this.size.values.length; i++) {
			if (this.size.values[i] !== undefined) {
				x.push(this.x.values[i]);
				y.push(this.y.values[i]);
			}
		}
		return {x: x, y: y}
	}

	_isPointMeetsCriteria(datum) {
		return this._isValidChartValue(this.x, datum[0])
			&& this._isValidChartValue(this.y, datum[1])
	}

	_isValidChartValue(axis, value) {
		if (!this._isValidAxisType(axis.type))
			return false;
		if (axis.type === COLUMN_TYPE.NUMBERS)
			return Number.isFinite(value);
		return true;
	}

	_isValidAxisType(type) {
		return type !== null && type !== COLUMN_TYPE.UNKNOWN;
	}
}